import React from "react";
import { Xiaohei } from "../actors";

/*
 * ILLUSTRATION-STYLE scenes (Anivaze v2) — a different visual LANGUAGE, not a
 * filter. The "Xiaohei" hand-drawn aesthetic (MIT, helloianneo/ian-xiaohei-
 * illustrations): minimalist black ink on white, slightly trembling lines, lots
 * of negative space, a character that ACTS out the idea, sparse red/blue accents.
 * This is the template for turning an illustration style into a "type of video".
 */

const HandDrawnDefs = () => (
  <filter id="hd-tremor" x="-6%" y="-6%" width="112%" height="112%" colorInterpolationFilters="sRGB">
    <feTurbulence type="fractalNoise" baseFrequency="0.016" numOctaves="2" seed="4" result="n" />
    <feDisplacementMap in="SourceGraphic" in2="n" scale="1.9" xChannelSelector="R" yChannelSelector="G" />
  </filter>
);

export interface IllustrationSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

const Caption = (label: string) => (
  <g transform="translate(400 416)">
    <text x={0} y={0} textAnchor="middle" fill="#1a1a1a" fontSize={20} fontWeight={700}
      fontFamily="'Caveat','Comic Sans MS','Segoe Print',Inter,sans-serif">{label}</text>
  </g>
);

/* ━━━ Xiaohei has a big idea (great for "what is…?" intros) ━━━━━━━━━━━━━━━━━ */
export const XiaoheiIdeaScene: React.FC<IllustrationSceneProps> = ({
  opacity = 1, accentColor = "#ef4444", t = 0, label = "A big idea — let's explore it!",
}) => {
  const ink = "#1a1a1a";
  const on = Math.sin(t * 2.5) > -0.3; // bulb mostly on, occasional flicker
  return (
    <g opacity={opacity}>
      <defs><HandDrawnDefs /></defs>
      <rect width={800} height={450} fill="#fbfbf6" />
      <g filter="url(#hd-tremor)" stroke={ink} strokeWidth={2.5} strokeLinecap="round" fill="none">
        <path d="M120,372 q280,9 560,0" />
        {/* lightbulb */}
        <g transform="translate(440 156)">
          <circle r={34} fill={on ? "#fde68a" : "#fffef5"} stroke={ink} strokeWidth={2.7} />
          <path d="M-11,30 h22 M-9,38 h18 M-6,45 h12" />
          <path d="M-9,4 q9,-15 18,0" strokeWidth={2} />
          {on && Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <path key={i} d={`M${Math.cos(a) * 46},${Math.sin(a) * 46} L${Math.cos(a) * 60},${Math.sin(a) * 60}`} strokeWidth={2.3} />;
          })}
        </g>
        {Xiaohei(296, 372, t, 1.5, "point")}
      </g>
      {/* sparse accents (the Xiaohei red/blue annotation marks) */}
      <path d="M610,120 l12,-12 M616,118 l9,3" stroke={accentColor} strokeWidth={2.2} fill="none" strokeLinecap="round" />
      <circle cx={158} cy={150} r={4} fill="#3b82f6" />
      <path d="M150,196 q8,6 16,0" stroke="#3b82f6" strokeWidth={2} fill="none" strokeLinecap="round" />
      {Caption(label)}
    </g>
  );
};

/* ━━━ Xiaohei walks the path (great for journeys / step-by-step) ━━━━━━━━━━━━ */
export const XiaoheiWalkScene: React.FC<IllustrationSceneProps> = ({
  opacity = 1, accentColor = "#ef4444", t = 0, label = "One step at a time…",
}) => {
  const ink = "#1a1a1a";
  const dash = (t * 55) % 44;
  return (
    <g opacity={opacity}>
      <defs><HandDrawnDefs /></defs>
      <rect width={800} height={450} fill="#fbfbf6" />
      <g filter="url(#hd-tremor)" stroke={ink} strokeWidth={2.5} strokeLinecap="round" fill="none">
        {/* sun outline */}
        <g transform="translate(665 92)">
          <circle r={24} />
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <path key={i} d={`M${Math.cos(a) * 30},${Math.sin(a) * 30} L${Math.cos(a) * 40},${Math.sin(a) * 40}`} strokeWidth={2.1} />;
          })}
        </g>
        {/* winding path */}
        <path d="M70,372 q330,12 670,-2" strokeWidth={2.7} />
        {Array.from({ length: 14 }, (_, i) => (
          <circle key={i} cx={70 + i * 48 - dash} cy={372 + Math.sin(i) * 2} r={1.6} fill={ink} stroke="none" />
        ))}
        {/* goal flag */}
        <g transform="translate(700 372)">
          <path d="M0,0 L0,-42" />
          <path d="M0,-42 l24,8 -24,8" strokeWidth={2.3} />
        </g>
        {Xiaohei(250, 372, t, 1.5, "walk")}
      </g>
      <path d="M150,150 l12,-10 M156,148 l8,3" stroke={accentColor} strokeWidth={2.2} fill="none" strokeLinecap="round" />
      {Caption(label)}
    </g>
  );
};
